import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  clearError,
  deleteTask,
  getTasks,
  updateTask,
} from "../../store/slices/tasksSlice";
import { taskAPI } from "../../services/api";
import { getPartners } from "../../store/slices/partnersSlice";
import { Task, TaskFilters, TaskStatus, TaskType } from "../../types/tasks";
import { Pagination } from "../../components/ListView/Pagination";
import { TaskListHeader, ViewMode } from "../../components/tasks/TaskListHeader";
import { TaskListFilters } from "../../components/tasks/TaskListFilters";
import { TaskTable } from "../../components/tasks/TaskTable";
import { TaskForm } from "../../components/tasks/TaskForm";
import { TaskKanban } from "../../components/tasks/TaskKanban";
import { TaskCalendar } from "../../components/tasks/TaskCalendar";
import { TaskGantt } from "../../components/tasks/TaskGantt";

type Tab = "all" | "todo" | "sales";

const applyTabToFilters = (tab: Tab, base: TaskFilters): TaskFilters => {
  if (tab === "all") return { ...base, type: undefined };
  return { ...base, type: [tab as TaskType] };
};

export const TasksListing: React.FC = () => {
  const { t } = useTranslation("tasks");
  const dispatch = useAppDispatch();
  const {
    tasks,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.tasks);

  const authUser = useAppSelector((state) => state.auth.user);
  const isAdmin = authUser?.role === "admin";
  const currentUserId = authUser?._id ?? "";

  const [activeTab, setActiveTab] = useState<Tab>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filters, setFilters] = useState<TaskFilters>({
    search: "",
    page: 1,
    limit: 20,
  });

  const storePartners = useAppSelector((state) => state.partner.partners);
  const [filterAssignees, setFilterAssignees] = useState<{ _id: string; firstName: string; lastName: string }[]>([]);

  useEffect(() => {
    if (!storePartners.length) {
      dispatch(getPartners({ limit: 200, page: 1 }));
    }
    taskAPI.getAssignees()
      .then((r) => setFilterAssignees(r.data.data?.users ?? []))
      .catch(() => {});
  }, []);

  // Kanban view fetches all tasks (no pagination) — limit bumped up
  const kanbanFilters = useMemo<TaskFilters>(() => ({
    ...filters,
    page: 1,
    limit: 500,
  }), [filters]);

  const prevSearch = useRef(filters.search);

  // Calendar and gantt need all tasks, same as kanban
  const needsAllTasks = viewMode === "kanban" || viewMode === "calendar" || viewMode === "gantt";

  const queryFilters = useMemo(
    () => applyTabToFilters(activeTab, needsAllTasks ? kanbanFilters : filters),
    [activeTab, filters, kanbanFilters, needsAllTasks],
  );

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    if (searchChanged && filters.search) {
      const handle = setTimeout(() => dispatch(getTasks(queryFilters)), 400);
      return () => clearTimeout(handle);
    }
    dispatch(getTasks(queryFilters));
  }, [dispatch, queryFilters, filters.search]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (partial: Partial<TaskFilters>) => {
    setFilters((prev) => ({ ...prev, ...partial, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ search: "", page: 1, limit: 20 });
    setActiveTab("all");
  };

  const handlePageChange = (p: number) => {
    setFilters((prev) => ({ ...prev, page: p }));
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("form.confirmDelete"))) return;
    try {
      await dispatch(deleteTask(id)).unwrap();
      toast.success(t("messages.deletedSuccess"));
    } catch {
      // error handled via slice
    }
  };

  const handleStatusChange = async (taskId: string, status: TaskStatus) => {
    try {
      await dispatch(updateTask({ id: taskId, data: { status } })).unwrap();
    } catch {
      // error handled via slice
    }
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setIsFormOpen(true);
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingTask(null);
  };

  const activeFilterCount = [
    filters.clientIds?.length,
    filters.assigneeIds?.length,
    filters.status?.length,
    filters.dateFrom ? 1 : 0,
    filters.dateTo ? 1 : 0,
    filters.hasStructuredForm ? 1 : 0,
    filters.overdueOnly ? 1 : 0,
  ].reduce<number>((sum, n) => sum + (n ?? 0), 0);

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  return (
    <div className="p-6">
      <TaskListHeader
        search={filters.search ?? ""}
        onSearchChange={(search) => handleFiltersChange({ search })}
        onNewTask={handleNewTask}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        filtersSlot={
          <TaskListFilters
            filters={filters}
            onChange={handleFiltersChange}
            onClear={handleClearFilters}
            activeCount={activeFilterCount}
            partners={storePartners}
            assignees={filterAssignees}
          />
        }
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {viewMode === "kanban" && (
        <div className="mt-4">
          <TaskKanban
            tasks={tasks}
            onEdit={handleEdit}
            onStatusChange={handleStatusChange}
          />
        </div>
      )}

      {viewMode === "calendar" && (
        <div className="mt-4">
          <TaskCalendar
            tasks={tasks}
            isAdmin={isAdmin}
            onEditTask={handleEdit}
          />
        </div>
      )}

      {viewMode === "gantt" && (
        <div className="mt-4">
          <TaskGantt
            tasks={tasks}
            isAdmin={isAdmin}
            onEditTask={handleEdit}
          />
        </div>
      )}

      {viewMode === "table" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <TaskTable
            tasks={tasks}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit ?? 20}
          />
        </div>
      )}

      <TaskForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        task={editingTask}
      />
    </div>
  );
};
